import { useState, useEffect, useRef, ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import icons
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
// import data
import businessUnitsData from "../../data/businessUnits.json";
import navUnitsData from "../../data/navUnits.json";
import { DataStructure, DataKeys, DataFlat } from '../../types';
// import context
import { useLang, useNavTab } from "../Context";
// import global var
import { typedVocab } from "../GlobalVar";
import {
    BUSINESS_UNIT_NAV_LOGOS,
    getBusinessUnitNavLogoSrc,
} from "./businessUnitNavLogos";
import {
    buildNavHref,
    isLightSubsidiaryPath,
    isNavPathActive,
} from "./navPathUtils";
import { HealthLang } from "../health/healthTypes";

const typedBusinessUnitsData : DataStructure = businessUnitsData;
const typedNavUnits : DataStructure = navUnitsData;

const businessUnitNavIconSlot =
    "flex h-5 w-5 shrink-0 items-center justify-center";

function BusinessUnitNavLogo({ filename }: { filename: string }) {
    const src = getBusinessUnitNavLogoSrc(filename);
    if (!src) {
        return <span className={businessUnitNavIconSlot} aria-hidden="true" />;
    }
    return (
        <span className={businessUnitNavIconSlot} aria-hidden="true">
            <img
                className="max-h-5 max-w-full object-contain"
                src={src}
                alt=""
                width={20}
                height={20}
                decoding="async"
            />
        </span>
    );
}

// Preload bundled nav unit logos when this module loads (dropdown open is faster).
void Object.values(BUSINESS_UNIT_NAV_LOGOS);

const NAV_ITEM_DEFAULT =
    "text-left lg:text-sm sm:text-base text-white cursor-pointer hover:text-teal-300";
const NAV_ITEM_ACTIVE =
    "text-left lg:text-sm sm:text-base text-teal-300 cursor-default";
const NAV_ITEM_BLOCK_DEFAULT =
    "block text-left text-white leading-snug break-words lg:text-sm sm:text-base cursor-pointer hover:text-teal-300";
const NAV_ITEM_BLOCK_ACTIVE =
    "block text-left text-teal-300 leading-snug break-words lg:text-sm sm:text-base cursor-default";

function closeNavDropdown(setNavTab: ReturnType<typeof useNavTab>["setNavTab"]) {
    setNavTab({ Name: "", Open: false, LeftPos: 0 });
}

function NavDropdownLink({
    href,
    pathname,
    exact = true,
    classNameDefault = NAV_ITEM_DEFAULT,
    classNameActive = NAV_ITEM_ACTIVE,
    children,
}: {
    href: string;
    pathname: string;
    exact?: boolean;
    classNameDefault?: string;
    classNameActive?: string;
    children: ReactNode;
}) {
    const { setNavTab } = useNavTab();
    const active = isNavPathActive(pathname, href, exact);
    if (active) {
        return (
            <span className={classNameActive} aria-current="page">
                {children}
            </span>
        );
    }
    return (
        <Link
            to={href}
            className={classNameDefault}
            onClick={() => closeNavDropdown(setNavTab)}
        >
            {children}
        </Link>
    );
}

function navHref(path: string, lang: HealthLang): string {
    return buildNavHref(path, lang);
}

interface NavToggleButtonProps {
    id: string;
    children: ReactNode;
    ariaLabel?: string;
};
export function NavToggleButton({ id, children, ariaLabel } : NavToggleButtonProps) {
    const componentRef = useRef<HTMLDivElement>(null);
    const { navTab, setNavTab } = useNavTab();
    const location = useLocation();
    const isOpen = navTab.Name === id && navTab.Open;
    const isLightSubsidiary = isLightSubsidiaryPath(location.pathname);
    const textColorClass = isLightSubsidiary ? "text-black" : "text-white";

    function setClickedOpen() {
        const nextOpen = !isOpen;
        const left = componentRef.current?.getBoundingClientRect().left ?? 0;
        setNavTab({
            Name: nextOpen ? id : "",
            Open: nextOpen,
            LeftPos: left,
        });
    };

    let style = isOpen
                ? `flex items-center justify-center gap-2 cursor-pointer select-none pb-1 border-b-1 ${
                      isLightSubsidiary ? "border-black" : "border-white"
                  }`
                : "flex items-center justify-center gap-2 cursor-pointer select-none pb-1 border-b-1 border-transparent"

    return (
        <div
            ref={componentRef}
            id={id}
            className={style}
            onClick={() => setClickedOpen()}
            role="button"
            aria-label={ariaLabel}
            aria-expanded={isOpen}
        >
            { children }
            <FontAwesomeIcon
                className={`${textColorClass} shrink-0 origin-center transition-transform duration-200 ease-out motion-reduce:transition-none ${
                    isOpen ? "scale-y-[-1]" : "scale-y-100"
                }`}
                icon={faChevronDown}
                aria-hidden="true"
            />
        </div>
    )
};

export function AllGraphen() {
    return (
        <div className="grid w-full min-w-0 grid-cols-4 gap-x-5 gap-y-8">
            <ShowAllBusinessUnits showIcons />
        </div>
    )
};

export function LanguageSelect() {
    const { lang, setLang } = useLang();

    let showLang = Object.keys(typedNavUnits["Language"]).map((i) => {
        let style = lang === i
                    ? "text-right cursor-pointer hover:text-teal-300 text-teal-300 "
                    : "text-right cursor-pointer hover:text-teal-300 text-white"
        return (
            <p id={i} className={style} onClick={() => setLang(i)}>
                {typedNavUnits["Language"][i]}
            </p>
        )
    });

    return (
        <div className="grid gap-2">
            <p className="text-right text-white font-bold">{typedVocab["Select Language"][lang]}</p>
            { showLang }
        </div>
    )
};

interface OtherTabsProps {
    tabId: string;
    anchorOffsetLeft?: number;
}

export function OtherTabs({ tabId, anchorOffsetLeft = 0 }: OtherTabsProps) {
    const { lang } = useLang();
    const dropdownList = typedNavUnits[tabId]?.[lang] ?? {};

    return (
        <div
            className="flex w-fit flex-col gap-2"
            style={{ marginLeft: anchorOffsetLeft }}
        >
            <p className="text-left text-white font-bold">{typedVocab[tabId][lang]}</p>
            <ShowUnits data={dropdownList} />
        </div>
    );
}

interface ShowAllBusinessUnitsProps {
    showIcons?: boolean;
    isFooter?: boolean;
};

/** Hidden from Business Units nav/footer only — keep entries in /all-solutions (e.g. Other). */
const HIDDEN_NAV_BUSINESS_UNITS = new Set(["security", "mobility"]);

export function ShowAllBusinessUnits({ showIcons, isFooter } : ShowAllBusinessUnitsProps) {
    const { lang } = useLang();
    const location = useLocation();
    const { setNavTab } = useNavTab();
    const hLang = lang as HealthLang;

    let showUnits = Object.keys(typedBusinessUnitsData)
        .filter((i) => !HIDDEN_NAV_BUSINESS_UNITS.has(i))
        .map((i: any) => {
        try {
            let data = typedBusinessUnitsData[i][lang] as DataKeys;
            let image = typedBusinessUnitsData[i]["Image"] as String;
            let title = Object.keys(data)[0];
            let unitList = data[title];
            const unitHref = navHref(`/${i}`, hLang);
            const isUnitLanding = isNavPathActive(
                location.pathname,
                unitHref,
                true
            );

            let showList = Object.keys(unitList).map((u) => {
                let label = unitList[u];
                const itemHref = navHref(`/${i}/${u}`, hLang);
                return (
                    <NavDropdownLink
                        key={u}
                        href={itemHref}
                        pathname={location.pathname}
                        exact
                        classNameDefault={NAV_ITEM_BLOCK_DEFAULT}
                        classNameActive={NAV_ITEM_BLOCK_ACTIVE}
                    >
                        {label}
                    </NavDropdownLink>
                );
            });

            let defaultStyle = "text-left font-bold cursor-pointer text-white hover:text-teal-300";
            let titleStyle = isUnitLanding
                ? "text-left font-bold cursor-default text-teal-300"
                : defaultStyle;

            return (
                <div key={i} className="min-w-0">
                    <div className="grid grid-flow-col auto-cols-max items-center gap-1 mb-2">
                        { showIcons && image ? (
                            <BusinessUnitNavLogo filename={String(image)} />
                        ) : null}
                        {isUnitLanding ? (
                            <span
                                className={`${isFooter ? defaultStyle : titleStyle} block leading-snug break-words`}
                                aria-current="page"
                            >
                                {title}
                            </span>
                        ) : (
                            <Link
                                to={unitHref}
                                className={`${isFooter ? defaultStyle : titleStyle} block leading-snug break-words`}
                                onClick={() => closeNavDropdown(setNavTab)}
                            >
                                {title}
                            </Link>
                        )}
                    </div>
                    <div className="grid gap-1">
                        { showList }
                    </div>
                </div>
            )
        } catch {
            return (
                <></>
            )
        }
    });

    return (
        <>
            { showUnits }
        </>
    )
};

interface ShowUnitsProps {
    data: DataKeys;
};
export function ShowUnits({ data } : ShowUnitsProps) {
    const { lang } = useLang();
    const location = useLocation();
    const hLang = lang as HealthLang;

    let showList = Object.keys(data).map((i) => {
        let label = data[i];
        const href = navHref(`/${i}`, hLang);
        return (
            <NavDropdownLink
                key={i}
                href={href}
                pathname={location.pathname}
                exact
            >
                {label}
            </NavDropdownLink>
        );
    });

    return (
        <>
            { showList }
        </>
    )
};


interface MobileDropdownUnitProps {
    id: string;
    label: string;
    toggle?: boolean;
};
export function MobileDropdownUnit({ id, label, toggle } : MobileDropdownUnitProps) {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    function toggleOpen() {
        if (toggle) {
            setIsOpen(!isOpen)
        };
    };

    return (
        <>
            <div id={id} className="w-full px-5 py-3 flex text-md cursor-pointer border-b-1 border-grey-300" onClick={toggleOpen}>
                <p className="text-left text-white select-none">{ label }</p>
                { toggle && (
                    <FontAwesomeIcon
                        className={`ml-auto shrink-0 origin-center text-white transition-transform duration-200 ease-out motion-reduce:transition-none ${
                            isOpen ? "scale-y-[-1]" : "scale-y-100"
                        }`}
                        icon={faChevronDown}
                        aria-hidden="true"
                    />
                )}
            </div>
            { isOpen && <MobileDropdownContent id={id} /> }
        </>
    )
};

interface MobileDropdownContentProps {
    id: string;
};
export function MobileDropdownContent({ id } : MobileDropdownContentProps) {
    const [content, setContent] = useState<ReactNode>(<></>)
    const { lang } = useLang();

    useEffect(() => {
        if (id === "Business Units") {
            setContent(<MobileAllGraphen />);
        } else {
            setContent(<DropdownList data={typedNavUnits[id][lang]} />)
        };
    }, [id]);


    return (
        <div className="px-10 py-5">
            { content }
        </div>
    )
};

export function MobileAllGraphen() {
    const { lang } = useLang();
    const location = useLocation();
    const hLang = lang as HealthLang;

    let showUnits = Object.keys(typedBusinessUnitsData)
        .filter((i) => !HIDDEN_NAV_BUSINESS_UNITS.has(i))
        .map((i: any) => {
        let data = typedBusinessUnitsData[i][lang] as DataKeys;
        let image = typedBusinessUnitsData[i]["Image"] as String;
        let title = Object.keys(data)[0];
        let unitList = data[title];
        const unitHref = navHref(`/${i}`, hLang);
        const isUnitLanding = isNavPathActive(location.pathname, unitHref, true);

        return (
            <Link to={unitHref}>
                <DropdownHeader title={title} image={image} active={isUnitLanding} />
                <div className="grid gap-1 mb-2">
                    <DropdownList data={unitList} parentLink={i} />
                </div>
            </Link>
        )
    });

    return (
        <>
            { showUnits }
        </>
    )
};

interface DropdownListProps {
    data: DataFlat;
    parentLink?: string;
};
export function DropdownList({ data, parentLink } : DropdownListProps) {
    const { lang } = useLang();
    const location = useLocation();
    const hLang = lang as HealthLang;

    let showList = Object.keys(data).map((i) => {
        let label = data[i];
        let link = parentLink ? `${parentLink}/${i}` : i;
        const href = navHref(`/${link}`, hLang);
        return (
            <NavDropdownLink
                key={i}
                href={href}
                pathname={location.pathname}
                exact
            >
                {label}
            </NavDropdownLink>
        );
    });

    return (
        <div className="grid gap-1 mb-2">
            { showList }
        </div>
    )
};

interface DropdownHeader {
    title: String;
    image?: String;
    active?: boolean;
};
export function DropdownHeader({ title, image, active } : DropdownHeader) {
    const titleClass = active
        ? "text-left text-teal-300 font-bold cursor-default"
        : "text-left text-white font-bold cursor-pointer hover:text-teal-300";
    return (
        <div className="grid grid-flow-col auto-cols-max items-center gap-1 mb-2 py-3 border-b-1 border-grey-300">
            { image ? <BusinessUnitNavLogo filename={String(image)} /> : null }
            {active ? (
                <span className={titleClass} aria-current="page">{title}</span>
            ) : (
                <p className={titleClass}>{title}</p>
            )}
        </div>
    )
};


